/* eslint-disable @typescript-eslint/naming-convention */
import { Location as BrowserLocation } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ModalController, Platform } from '@ionic/angular';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IosPwaPromptComponent } from './ios-pwa-prompt/ios-pwa-prompt.component';
import { User } from './models/user';
import { DesktopRedirectService } from './services/desktop-redirect.service';
import { FontsizeService } from './services/fontsize.service';
import { LanguageService } from './services/language.service';
import { TranslationService } from './services/translation.service';
import { UserService } from './services/user.service';
import { PushNotificationService } from './services/push-notification.service';
import { AnnouncementTypeType } from './models/announcement';
import { LoadingService } from './services/loading.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  user: User;
  isIframe = false;
  loginDisplay = false;
  fsValue: string;
  currentFontSize: number;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private userService: UserService,
    private languageService: LanguageService,
    private router: Router,
    private browserLocation: BrowserLocation,
    private gtmService: GoogleTagManagerService,
    private platform: Platform,
    private modalController: ModalController,
    private activatedRoute: ActivatedRoute,
    private fontSizeService: FontsizeService,
    private translationService: TranslationService,
    private desktopRedirectService: DesktopRedirectService,
    private pushNotificationService: PushNotificationService,
    private loadingService: LoadingService,
  ) {
    this.loadingService.setLoading(true);
    this.translationService.initTranslations().finally(() => {
      this.loadingService.setLoading(false);
    });

    this.activatedRoute.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      if (params) {
        if (params.get('fromFirebaseSw')) {
          console.log('fromFirebaseSw');
          const userLanguage = this.userService.user.language;
          const deviceLanguage = navigator.language;
          const newLanguageParameter = userLanguage ?? deviceLanguage.slice(0, 2);
          const url = this.router.url.split('/');
          url[1] = newLanguageParameter;
          url.shift();
          let navigateToUrl = url.join('/');
          navigateToUrl = navigateToUrl.replace('&fromFirebaseSw=true', '');
          this.router.navigateByUrl(navigateToUrl);
        }
      }
    });

    this.user = this.userService.getUser();

    // Set default options for push notification tags
    const hasPushNotificationTags = this.user.userdata?.push_notification?.tags;
    if (!hasPushNotificationTags || hasPushNotificationTags.length === 0) {
      this.userService.setPushNotification({ tags: [AnnouncementTypeType.WARNING] });
    }
    this.pushNotificationService.initPushNotifications();

    if (this.user.language) {
      this.languageService.setLanguage(this.user.language);
    }

    // If the user agent is marked with "Mobi", it is probably a mobile device.
    // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
    if (!/Mobi/i.test(navigator.userAgent)) {
      this.desktopRedirectService.showDesktopRedirect(this.user.showDesktopRedirect);
    }
  }

  getCookie(cname) {
    const name = cname + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  checkSavedFont() {
    const fontSize = this.getCookie('fontSize');
    if (fontSize !== '') {
      return parseInt(fontSize, 10);
    }
    //default to smallest font size
    return 16;
  }

  initFontSize() {
    const currentFont = this.checkSavedFont();
    document.getElementsByTagName('html')[0].style.fontSize = `${currentFont}px`;
  }

  ngOnInit() {
    this.initFontSize();
    this.isIframe = window !== window.parent && !window.opener;

    const platforms = this.platform.platforms();
    const isInStandaloneMode = 'standalone' in window.navigator && window.navigator['standalone'];
    const isPrivacyPolicy =
      this.browserLocation.path().split('/').pop() === 'privacy-policy' ? true : false;
    //Check if modal has been already dismissed
    const isDismissed = document.cookie.match(/^(.*;)?\s*pwaDismissed\s*=\s*[^;]+(.*)?$/);
    if (platforms.includes('ios') && !isInStandaloneMode && !isPrivacyPolicy && !isDismissed) {
      setTimeout(() => this.presentModal(), 1000);
    }

    const gtm = document.getElementById('GTMscript');
    if (!gtm) {
      this.gtmService.addGtmToDom().then(() => {
        this.router.events.forEach((item) => {
          if (item instanceof NavigationEnd) {
            const gtmTag = {
              event: 'page',
              pageName: item.url,
            };
            this.gtmService.pushTag(gtmTag);
          }
        });
      });
    }

    window.addEventListener(
      'CookiebotOnConsentReady',
      () => {
        // Send if browser or pwa
        let isPwa = false;
        const mqStandAlone = '(display-mode: standalone)';
        if (window.navigator['standalone'] || window.matchMedia(mqStandAlone).matches) {
          isPwa = true;
        }
        this.gtmService
          .pushTag({
            event: 'isPWA',
            data: isPwa,
          })
          .catch(() => {
            // Ignore gtm push tag error. Maybe ad block blocked it
          });
      },
      false,
    );

    this.fontSizeService.userFontSize$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((message: string) => {
        this.fsValue = message;
      });
  }

  async presentModal() {
    const modal = await this.modalController.create({
      component: IosPwaPromptComponent,
      cssClass: 'ios-promt',
      swipeToClose: true,
      mode: 'ios',
    });
    return await modal.present();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
